/**
 * Round simulation (v0.3).
 *
 * Fixed-timestep, deterministic. All randomness from a single mulberry32 stream
 * seeded with `fnv1a(dayId)`.
 *
 * v0.3 changes:
 *  - Perch surfaces (ground + fence top). Food lands on whichever surface its
 *    falling trajectory crosses first. Birds choose perches on surfaces too.
 *  - Birds are motivated by food. When a pellet lands within their notice radius
 *    they claim it, fly to it, peck, then look for another or rest. This replaces
 *    the v0.2 "consume any nearby food on a 20-tick timer" rule.
 *  - Birds use continuous velocity-based motion with arrival easing, so the
 *    renderer can derive facing/pose from current velocity.
 */

import type { DayMeta } from '../data/dayMeta';
import {
  BIRD_STAY_SECONDS,
  CALM_MULT_MAX,
  CALM_MULT_MIN,
  FOOD_TTL_SECONDS,
  PLAY_TICKS,
  REVEAL_TICKS,
  SCORE_FEED,
  SCORE_VISIT,
  SCORE_WASTE_PENALTY,
  SIM_DT,
  SIM_HZ,
  TOTAL_TICKS,
  VISIT_QUALIFY_TICKS,
  windDriftPxPerSecond,
} from './constants';
import { mulberry32, fnv1a, randRange } from './rng';

export type Phase = 'reveal' | 'play' | 'ended';
export type BirdMode = 'approaching' | 'perched' | 'leaving';

export interface InputEvent {
  tick: number;
  x: number;
  y: number;
  foodId: string;
}

export interface Bird {
  id: number;
  speciesId: string;
  arrivedAt: number;
  leavesAt: number;
  age: number;
  /** Current position (world px). */
  x: number;
  y: number;
  /** Current velocity (world px / sec). */
  vx: number;
  vy: number;
  /** Current flight target. */
  targetX: number;
  targetY: number;
  /** Off-screen entry/exit points (for departure direction). */
  entryX: number;
  entryY: number;
  exitX: number;
  exitY: number;
  mode: BirdMode;
  /** Initial perch position (set at spawn from schedule). */
  perchX: number;
  perchY: number;
  pecks: number;
  /** Tick at which last peck happened (for feed-pose duration). */
  lastPeckAt: number;
  /** Tick at which the bird may next scan for food (anti-spam). */
  scanCooldownUntil: number;
  /** Index into Round.state.food for the pellet this bird has claimed (-1 if none). */
  targetFoodIdx: number;
  visitQualified: boolean;
  gone: boolean;
}

export interface FoodPellet {
  foodId: string;
  x: number;
  y: number;
  vx: number;
  vy: number;
  groundY: number;
  landed: boolean;
  ttl: number;
  consumed: boolean;
  /** Bird id that has claimed this pellet (-1 if free). */
  claimedBy: number;
}

interface ArrivalSlot {
  speciesId: string;
  tick: number;
  perchX: number;
  perchY: number;
  entrySide: -1 | 1;
}

export interface PerchSurface {
  /** World-y of the perchable top of this surface. */
  y: number;
  xMin: number;
  xMax: number;
  /** Sorting priority: higher = preferred (fence > ground). */
  priority: number;
}

export interface RoundState {
  phase: Phase;
  tick: number;
  score: number;
  pouch: Map<string, number>;
  birds: Bird[];
  food: FoodPellet[];
  wasted: number;
  pecks: number;
  qualifiedVisits: number;
}

/** Tunables for bird motion. */
const BIRD_MAX_SPEED = 320;        // world px / sec
const BIRD_ACCEL = 900;            // world px / sec^2
const BIRD_ARRIVE_RADIUS = 12;     // px from target = "arrived"
const BIRD_SLOWDOWN_RADIUS = 80;   // start easing inside this radius
const BIRD_FOOD_NOTICE = 360;      // px: how far a bird notices food
const BIRD_REST_TICKS_MIN = 35;
const BIRD_REST_TICKS_MAX = 95;
const FEED_POSE_TICKS = 22;

export class Round {
  readonly meta: DayMeta;
  readonly width: number;
  readonly height: number;
  readonly windPxPerSec: number;
  readonly windDirRad: number;
  readonly groundY: number;
  readonly surfaces: PerchSurface[];
  private readonly rng: () => number;
  private readonly schedule: ArrivalSlot[];
  private nextArrivalIdx = 0;
  private nextBirdId = 1;
  private inputQueue: InputEvent[] = [];

  state: RoundState;

  constructor(meta: DayMeta, width: number, height: number) {
    this.meta = meta;
    this.width = width;
    this.height = height;
    this.rng = mulberry32(fnv1a(meta.id));
    this.windPxPerSec = windDriftPxPerSecond(meta.weather.wind_speed_norm);
    this.windDirRad = (meta.weather.wind_direction_deg * Math.PI) / 180;
    this.groundY = Math.round(height * 0.78);

    // Perch surfaces. Highest-priority first.
    // Fence post top (right side of the D01 scene).
    const fenceY = Math.round(height * 0.59);
    const fenceXMin = Math.round(width * 0.685);
    const fenceXMax = Math.round(width * 0.725);
    this.surfaces = [
      { y: fenceY, xMin: fenceXMin, xMax: fenceXMax, priority: 2 },
      { y: this.groundY, xMin: 0, xMax: width, priority: 1 },
    ];

    this.schedule = this.bakeSchedule();
    this.state = this.freshState();
  }

  private bakeSchedule(): ArrivalSlot[] {
    // Collect (species_id) per slot first; we assign ticks after, with a max-gap rule.
    const speciesQueue: string[] = [];
    for (const sp of this.meta.species) {
      for (let i = 0; i < sp.target_visits; i++) speciesQueue.push(sp.species_id);
    }
    // Shuffle (Fisher-Yates) so species are interleaved instead of clumped per species.
    for (let i = speciesQueue.length - 1; i > 0; i--) {
      const j = Math.floor(this.rng() * (i + 1));
      const tmp = speciesQueue[i]!;
      speciesQueue[i] = speciesQueue[j]!;
      speciesQueue[j] = tmp;
    }

    const earliest = REVEAL_TICKS + 30;
    const latest = REVEAL_TICKS + Math.floor(PLAY_TICKS * 0.85);
    const span = Math.max(1, latest - earliest);
    const minGap = Math.max(1, Math.floor(0.6 * SIM_HZ));         // >= 0.6 s between arrivals
    const maxGap = Math.max(minGap + 1, Math.floor(4.0 * SIM_HZ)); // <= 4.0 s between arrivals
    // Even spacing as a baseline; jitter inside [minGap, maxGap].
    const baseStep = Math.max(minGap, Math.min(maxGap, Math.floor(span / speciesQueue.length)));

    const slots: ArrivalSlot[] = [];
    let cursor = earliest;
    for (let i = 0; i < speciesQueue.length; i++) {
      const speciesId = speciesQueue[i]!;
      const tick = i === 0
        ? earliest
        : Math.min(latest, cursor + minGap + Math.floor(this.rng() * (maxGap - minGap)));
      cursor = tick;

      const useFence = this.rng() < 0.18;
      let perchX: number;
      let perchY: number;
      if (useFence) {
        const s = this.surfaces[0]!;
        perchX = randRange(this.rng, s.xMin + 4, s.xMax - 4);
        perchY = s.y;
      } else {
        const s = this.surfaces[1]!;
        perchX = randRange(this.rng, this.width * 0.1, this.width * 0.9);
        perchY = s.y;
      }
      const entrySide: -1 | 1 = this.rng() < 0.5 ? -1 : 1;
      slots.push({ speciesId, tick, perchX, perchY, entrySide });
    }
    // baseStep is informational only; logic uses min/max gap. Silence unused warning.
    void baseStep;
    slots.sort((a, b) => a.tick - b.tick);
    return slots;
  }

  private freshState(): RoundState {
    const pouch = new Map<string, number>();
    for (const p of this.meta.pouch) pouch.set(p.food_id, p.count);
    return {
      phase: 'reveal',
      tick: 0,
      score: 0,
      pouch,
      birds: [],
      food: [],
      wasted: 0,
      pecks: 0,
      qualifiedVisits: 0,
    };
  }

  reset(): void {
    this.nextArrivalIdx = 0;
    this.nextBirdId = 1;
    this.inputQueue = [];
    this.state = this.freshState();
  }

  queueInput(ev: InputEvent): void {
    this.inputQueue.push(ev);
  }

  liveScore(): number {
    const s = this.state;
    const base = s.qualifiedVisits * SCORE_VISIT + s.pecks * SCORE_FEED;
    const waste = s.wasted * SCORE_WASTE_PENALTY;
    const ratio = s.pecks > 0 ? s.pecks / (s.pecks + s.wasted) : 0;
    const calm = CALM_MULT_MIN + (CALM_MULT_MAX - CALM_MULT_MIN) * ratio;
    return Math.max(0, Math.round((base + waste) * calm));
  }

  secondsRemaining(): number {
    return Math.max(0, (TOTAL_TICKS - this.state.tick) / SIM_HZ);
  }

  /** Pick a landing surface for food given its current x/falling trajectory. */
  private surfaceForFood(x: number): PerchSurface {
    // Surfaces are sorted highest-priority first.
    for (const s of this.surfaces) {
      if (x >= s.xMin && x <= s.xMax) return s;
    }
    return this.surfaces[this.surfaces.length - 1]!;
  }

  step(): void {
    const s = this.state;
    if (s.phase === 'ended') return;

    s.tick++;
    if (s.phase === 'reveal' && s.tick >= REVEAL_TICKS) s.phase = 'play';

    // 1. Inputs → spawn food pellets.
    if (s.phase === 'play') {
      for (const ev of this.inputQueue) {
        const remaining = s.pouch.get(ev.foodId) ?? 0;
        if (remaining > 0) {
          s.pouch.set(ev.foodId, remaining - 1);
          const surf = this.surfaceForFood(ev.x);
          s.food.push({
            foodId: ev.foodId,
            x: ev.x,
            y: ev.y,
            vx: 0,
            vy: 0,
            groundY: surf.y,
            landed: false,
            ttl: FOOD_TTL_SECONDS * SIM_HZ,
            consumed: false,
            claimedBy: -1,
          });
        }
      }
      this.inputQueue.length = 0;
    }

    // 2. Arrivals.
    while (
      this.nextArrivalIdx < this.schedule.length &&
      this.schedule[this.nextArrivalIdx]!.tick <= s.tick
    ) {
      const slot = this.schedule[this.nextArrivalIdx]!;
      const entryX = slot.entrySide === -1 ? -80 : this.width + 80;
      const entryY = slot.perchY - 80 - this.rng() * 60;
      const exitSide: -1 | 1 = this.rng() < 0.5 ? -1 : 1;
      const exitX = exitSide === -1 ? -80 : this.width + 80;
      const exitY = slot.perchY - 120 - this.rng() * 80;
      s.birds.push({
        id: this.nextBirdId++,
        speciesId: slot.speciesId,
        arrivedAt: s.tick,
        leavesAt: s.tick + BIRD_STAY_SECONDS * SIM_HZ,
        age: 0,
        x: entryX,
        y: entryY,
        vx: 0,
        vy: 0,
        targetX: slot.perchX,
        targetY: slot.perchY,
        entryX,
        entryY,
        exitX,
        exitY,
        mode: 'approaching',
        perchX: slot.perchX,
        perchY: slot.perchY,
        pecks: 0,
        lastPeckAt: -9999,
        scanCooldownUntil: 0,
        targetFoodIdx: -1,
        visitQualified: false,
        gone: false,
      });
      this.nextArrivalIdx++;
    }

    // 3. Food physics.
    const windVx = Math.cos(this.windDirRad) * this.windPxPerSec * SIM_DT;
    const gravity = 90;
    for (const f of s.food) {
      if (f.consumed) continue;
      if (!f.landed) {
        f.vy += gravity * SIM_DT;
        f.x += (f.vx + windVx) * SIM_DT * 60; // scale to original v0.2 motion
        f.y += f.vy;
        // Re-pick surface in case wind drifted the pellet into the fence column.
        const surf = this.surfaceForFood(f.x);
        f.groundY = surf.y;
        if (f.y >= f.groundY) {
          f.y = f.groundY;
          f.landed = true;
          f.vx = 0;
          f.vy = 0;
        }
        if (f.x < -10 || f.x > this.width + 10) {
          f.consumed = true;
          s.wasted++;
          continue;
        }
      }
      f.ttl--;
      if (f.ttl <= 0) {
        f.consumed = true;
        if (f.claimedBy !== -1) {
          // Free any claim so its bird re-decides.
          const owner = s.birds.find((b) => b.id === f.claimedBy);
          if (owner) { owner.targetFoodIdx = -1; }
        }
        s.wasted++;
      }
    }

    // 4. Bird behaviour.
    for (const b of s.birds) {
      if (b.gone) continue;
      b.age++;

      if (!b.visitQualified && b.age >= VISIT_QUALIFY_TICKS) {
        b.visitQualified = true;
        s.qualifiedVisits++;
      }

      // Mode transitions.
      const shouldLeave = s.tick >= b.leavesAt;
      if (shouldLeave && b.mode !== 'leaving') {
        b.mode = 'leaving';
        b.targetX = b.exitX;
        b.targetY = b.exitY;
        if (b.targetFoodIdx !== -1) {
          const claimed = s.food[b.targetFoodIdx];
          if (claimed && claimed.claimedBy === b.id) claimed.claimedBy = -1;
          b.targetFoodIdx = -1;
        }
      }

      // Decide a goal.
      if (b.mode === 'perched' && !shouldLeave && s.tick >= b.scanCooldownUntil) {
        // Look for the nearest unclaimed landed pellet within notice range.
        let bestIdx = -1;
        let bestD2 = BIRD_FOOD_NOTICE * BIRD_FOOD_NOTICE;
        for (let i = 0; i < s.food.length; i++) {
          const f = s.food[i]!;
          if (f.consumed || !f.landed || f.claimedBy !== -1) continue;
          const dx = f.x - b.x;
          const dy = f.y - b.y;
          const d2 = dx * dx + dy * dy;
          if (d2 < bestD2) { bestD2 = d2; bestIdx = i; }
        }
        if (bestIdx !== -1) {
          const f = s.food[bestIdx]!;
          f.claimedBy = b.id;
          b.targetFoodIdx = bestIdx;
          b.targetX = f.x;
          b.targetY = f.y;            // bird lands AT the pellet
          b.mode = 'approaching';
        } else {
          // Idle stroll: occasionally nudge to a nearby perch spot.
          if (this.rng() < 0.01) {
            const surf = b.targetY === this.surfaces[0]!.y ? this.surfaces[0]! : this.surfaces[1]!;
            const drift = randRange(this.rng, -90, 90);
            b.targetX = Math.max(surf.xMin + 4, Math.min(surf.xMax - 4, b.x + drift));
            b.targetY = surf.y;
            b.mode = 'approaching';
          }
          b.scanCooldownUntil = s.tick + 8;
        }
      }

      // Steering toward target.
      const dx = b.targetX - b.x;
      const dy = b.targetY - b.y;
      const dist = Math.hypot(dx, dy);

      if (b.mode === 'approaching') {
        // Desired speed scales down as we close in (arrival behaviour).
        const speedScale = Math.min(1, dist / BIRD_SLOWDOWN_RADIUS);
        const desiredSpeed = BIRD_MAX_SPEED * speedScale;
        const desiredVx = dist > 0.01 ? (dx / dist) * desiredSpeed : 0;
        const desiredVy = dist > 0.01 ? (dy / dist) * desiredSpeed : 0;
        // Accelerate toward desired.
        const maxDelta = BIRD_ACCEL * SIM_DT;
        const ddx = desiredVx - b.vx;
        const ddy = desiredVy - b.vy;
        const dMag = Math.hypot(ddx, ddy);
        if (dMag > maxDelta) {
          const k = maxDelta / dMag;
          b.vx += ddx * k;
          b.vy += ddy * k;
        } else {
          b.vx = desiredVx;
          b.vy = desiredVy;
        }
      } else if (b.mode === 'leaving') {
        // Always full speed outward.
        const desiredVx = dist > 0.01 ? (dx / dist) * BIRD_MAX_SPEED : 0;
        const desiredVy = dist > 0.01 ? (dy / dist) * BIRD_MAX_SPEED : 0;
        const maxDelta = BIRD_ACCEL * SIM_DT;
        const ddx = desiredVx - b.vx;
        const ddy = desiredVy - b.vy;
        const dMag = Math.hypot(ddx, ddy);
        if (dMag > maxDelta) {
          const k = maxDelta / dMag;
          b.vx += ddx * k;
          b.vy += ddy * k;
        } else {
          b.vx = desiredVx;
          b.vy = desiredVy;
        }
      } else {
        // perched → decay velocity to zero.
        b.vx *= 0.65;
        b.vy *= 0.65;
        if (Math.abs(b.vx) < 0.5) b.vx = 0;
        if (Math.abs(b.vy) < 0.5) b.vy = 0;
      }

      // Integrate.
      b.x += b.vx * SIM_DT;
      b.y += b.vy * SIM_DT;

      // Arrival check.
      if (b.mode === 'approaching' && dist <= BIRD_ARRIVE_RADIUS) {
        if (b.targetFoodIdx !== -1) {
          const f = s.food[b.targetFoodIdx];
          if (f && !f.consumed) {
            f.consumed = true;
            b.pecks++;
            s.pecks++;
            b.lastPeckAt = s.tick;
          }
          b.targetFoodIdx = -1;
          // After eating, snap to surface y and rest briefly before scanning again.
          b.y = b.targetY;
        }
        b.mode = 'perched';
        b.scanCooldownUntil = s.tick + Math.floor(
          BIRD_REST_TICKS_MIN + this.rng() * (BIRD_REST_TICKS_MAX - BIRD_REST_TICKS_MIN),
        );
      }

      if (b.mode === 'leaving') {
        // Off-screen?
        if (b.x < -60 || b.x > this.width + 60 || b.y < -60) {
          b.gone = true;
          if (b.targetFoodIdx !== -1) {
            const f = s.food[b.targetFoodIdx];
            if (f && f.claimedBy === b.id) f.claimedBy = -1;
            b.targetFoodIdx = -1;
          }
        }
      }
    }

    if (s.tick % 60 === 0) {
      s.birds = s.birds.filter((b) => !b.gone);
    }

    if (s.tick >= TOTAL_TICKS) {
      s.phase = 'ended';
      s.score = this.liveScore();
    }
  }
}

export { FEED_POSE_TICKS };
